import { useEffect, useState, useMemo } from "react";
import { FILTERS_CONFIG } from "./GlobalFilterPanel";
import { getFilterRelationshipsAPI } from "../../../api/reports-api";
import {
  buildCascadedDropdownOptions,
  buildBaseOptionsFromRelationships,
  normalizeRelationshipRows,
  sortFilterOptions,
  sortDropdownOptionsMap,
  CASCADE_FILTER_KEYS,
  PRESERVE_ORDER_KEYS,
} from "./filterRelationshipUtils";

const RELATIONSHIP_FILTER_KEYS = new Set(CASCADE_FILTER_KEYS);

/** Load options for filters not covered by filter_relationships (year, month, indirect, …). */
export async function loadBaseFilterOptions(optionKeys) {
  const opts = {};
  FILTERS_CONFIG.forEach((f) => {
    if (f.options) opts[f.key] = PRESERVE_ORDER_KEYS.has(f.key) ? f.options : sortFilterOptions(f.options, f.key);
  });

  const apiBacked = FILTERS_CONFIG.filter(
    (f) => f.apiFn && !RELATIONSHIP_FILTER_KEYS.has(f.key) && (!optionKeys || optionKeys.includes(f.key)),
  );
  const results = await Promise.all(
    apiBacked.map((f) =>
      f.apiFn()
        .then((res) => {
          const deduped = [
            ...new Set(
              (res.data || [])
                .map(f.apiMap)
                .filter((v) => v !== null && v !== undefined && String(v).trim() !== ""),
            ),
          ];
          return { key: f.key, options: PRESERVE_ORDER_KEYS.has(f.key) ? deduped : sortFilterOptions(deduped, f.key) };
        })
        .catch(() => ({ key: f.key, options: [] })),
    ),
  );
  results.forEach(({ key, options }) => {
    opts[key] = options;
  });
  return sortDropdownOptionsMap(opts);
}

/**
 * Session-cached filter_relationships feed + cascaded dropdown options.
 * @param draftFilters — current filter draft (drives cascade narrowing)
 * @param visibleKeys — keys shown on the active tab (null = all cascade keys)
 */
export function useReportFilterOptions(draftFilters, visibleKeys = null) {
  const [baseOptions, setBaseOptions] = useState({});
  const [relationshipRows, setRelationshipRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadBaseFilterOptions(null),
      getFilterRelationshipsAPI().then((res) => {
        if (res?.status && Number(res.status) !== 200) return [];
        return normalizeRelationshipRows(res?.data ?? res);
      }),
    ]).then(([otherOpts, rows]) => {
      if (cancelled) return;
      const relOpts = buildBaseOptionsFromRelationships(rows);
      setBaseOptions(sortDropdownOptionsMap({ ...otherOpts, ...relOpts }));
      setRelationshipRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const dropdownOptions = useMemo(
    () => buildCascadedDropdownOptions(relationshipRows, baseOptions, draftFilters, visibleKeys),
    [relationshipRows, baseOptions, draftFilters, visibleKeys],
  );

  return { dropdownOptions, relationshipRows, baseOptions };
}
